import {
  Baby, Clock, TrendingUp, Brain, Leaf, Smile,
  Stethoscope, Activity, GraduationCap, Users, Eye, Sparkles,
} from 'lucide-react';
import { MODULES } from '@/components/layout/modules.config';

// Presets de módulos/antecedentes por tipo de clínica. Compartido entre ClinicaPage
// (dispara el preset al cambiar clinica.tipoClinica) y ConfiguracionPage (muestra/edita
// los toggles resultantes). clinica.tipoClinica es la única fuente de verdad para el tipo;
// configuracion.perfilTipo se mantiene en sync automáticamente al guardar en ClinicaPage.

// Catálogo único de tipos de clínica / perfiles médicos — fuente de verdad para el selector
// en ClinicaPage, el badge de tipo y cualquier otra pantalla que liste o filtre por tipo
// (ClinicaDetailDialog, SuperAdminClinicasPage). Mantener en sync con clinica.entity.ts
// (TipoClinica) y configuracion-clinica.entity.ts (PerfilTipo) — ambos son el mismo ENUM.
// `description` resume en una línea qué se activa/oculta con este preset (ver CLINIC_PRESETS
// y ANTECEDENTES_PRESETS abajo), para que el selector sea autoexplicativo.
export type PerfilClinica = typeof TIPOS_CLINICA[number]['value'];

export const TIPOS_CLINICA = [
  {
    value: 'dental', label: 'Dental (Odontología / Estomatología)', color: 'bg-blue-100 text-blue-700', icon: Smile,
    description: 'Activa odontograma y periodontograma. Oculta antecedentes gineco-obstétricos y psiquiátricos.',
  },
  {
    value: 'medica_general', label: 'Médica General', color: 'bg-green-100 text-green-700', icon: Stethoscope,
    description: 'Todos los módulos y antecedentes disponibles, sin restricciones.',
  },
  {
    value: 'fisioterapia', label: 'Fisioterapia y Rehabilitación', color: 'bg-orange-100 text-orange-700', icon: Activity,
    description: 'Enfocado en rehabilitación. Oculta antecedentes gineco-obstétricos y psiquiátricos.',
  },
  {
    value: 'nutricion', label: 'Nutrición', color: 'bg-lime-100 text-lime-700', icon: Leaf,
    description: 'Antecedente de dieta activo. Oculta gineco-obstétricos y psiquiátricos.',
  },
  {
    value: 'especialidad', label: 'Especialidad (Tronco Común + Adaptabilidad)', color: 'bg-purple-100 text-purple-700', icon: GraduationCap,
    description: 'Base flexible — todos los módulos activos, ajustables a tu especialidad.',
  },
  {
    value: 'multidisciplinaria', label: 'Multidisciplinaria', color: 'bg-primary-100 text-primary-700', icon: Users,
    description: 'Todos los módulos y antecedentes, para equipos con varias especialidades.',
  },
  {
    value: 'psicologia', label: 'Psicología y Psiquiatría', color: 'bg-indigo-100 text-indigo-700', icon: Brain,
    description: 'Antecedentes psiquiátricos como eje central del expediente.',
  },
  {
    value: 'oftalmologia', label: 'Oftalmología / Optometría', color: 'bg-cyan-100 text-cyan-700', icon: Eye,
    description: 'Módulos clínicos generales. Oculta antecedentes no relacionados.',
  },
  {
    value: 'dermatologia', label: 'Dermatología / Medicina Estética', color: 'bg-pink-100 text-pink-700', icon: Sparkles,
    description: 'Módulos clínicos generales. Oculta antecedentes no relacionados.',
  },
] as const;

export const COLOR_TIPO: Record<string, string> = Object.fromEntries(TIPOS_CLINICA.map(t => [t.value, t.color]));

export const ALWAYS_ON = new Set(['dashboard', 'configuracion', 'vademecum', 'tareas']);
export const TOGGLEABLE = MODULES.filter(m => !ALWAYS_ON.has(m.id));
export const ALWAYS_ON_MODULES = MODULES.filter(m => ALWAYS_ON.has(m.id));

// Herramientas clínico-específicas que no son módulos de navegación (viven dentro de
// Pacientes › Exploraciones), pero comparten el mismo modulosHabilitadosJson que lee
// ModuleAccessGuard en el backend (ver backend/src/common/constants/modulos.constant.ts).
// Solo aplican si el perfil de la clínica es 'dental' o 'multidisciplinaria'.
export const HERRAMIENTAS_DENTALES = [
  { id: 'odontograma',      label: 'Odontograma',      icon: Smile },
  { id: 'periodontograma',  label: 'Periodontograma',  icon: Smile },
] as const;

export const ALL_ENABLED = {
  ...Object.fromEntries(TOGGLEABLE.map(m => [m.id, true])),
  ...Object.fromEntries(HERRAMIENTAS_DENTALES.map(h => [h.id, true])),
};

export const CLINIC_PRESETS: Record<string, Record<string, boolean>> = {
  dental:            { ...ALL_ENABLED },
  medica_general:    { ...ALL_ENABLED },
  fisioterapia:      { ...ALL_ENABLED, red: false },
  nutricion:         { ...ALL_ENABLED, red: false },
  especialidad:      { ...ALL_ENABLED },
  multidisciplinaria:{ ...ALL_ENABLED },
  psicologia:        { ...ALL_ENABLED },
  oftalmologia:      { ...ALL_ENABLED },
  dermatologia:      { ...ALL_ENABLED },
};

// Secciones de antecedentes del paciente (por tipo de clínica) — solo las secciones que
// no aplican a todos los perfiles son configurables aquí. Vacunación, alergias, patológicos,
// no patológicos y heredofamiliares siempre están activas.
export const ANTECEDENTES_TOGGLEABLE = [
  { id: 'gineco',        label: 'Gineco-Obstétrico',   icon: Baby },
  { id: 'perinatales',   label: 'Perinatales',         icon: Clock },
  { id: 'postnatales',   label: 'Postnatales',         icon: TrendingUp },
  { id: 'psiquiatricos', label: 'Psiquiátricos',       icon: Brain },
  { id: 'dieta',         label: 'Dieta Nutricional',   icon: Leaf },
] as const;

export const ALL_ANTECEDENTES_ENABLED = Object.fromEntries(ANTECEDENTES_TOGGLEABLE.map(s => [s.id, true]));

export const ANTECEDENTES_PRESETS: Record<string, Record<string, boolean>> = {
  dental:             { ...ALL_ANTECEDENTES_ENABLED, gineco: false, perinatales: false, postnatales: false, psiquiatricos: false, dieta: false },
  medica_general:     { ...ALL_ANTECEDENTES_ENABLED },
  fisioterapia:       { ...ALL_ANTECEDENTES_ENABLED, gineco: false, perinatales: false, postnatales: false, psiquiatricos: false },
  nutricion:          { ...ALL_ANTECEDENTES_ENABLED, gineco: false, perinatales: false, postnatales: false, psiquiatricos: false },
  especialidad:       { ...ALL_ANTECEDENTES_ENABLED },
  multidisciplinaria: { ...ALL_ANTECEDENTES_ENABLED },
  // Psiquiátricos es el eje central de este perfil — se mantiene activo a diferencia del resto.
  psicologia:         { ...ALL_ANTECEDENTES_ENABLED, gineco: false, perinatales: false, postnatales: false, dieta: false },
  oftalmologia:       { ...ALL_ANTECEDENTES_ENABLED, gineco: false, perinatales: false, postnatales: false, psiquiatricos: false, dieta: false },
  dermatologia:       { ...ALL_ANTECEDENTES_ENABLED, gineco: false, perinatales: false, postnatales: false, psiquiatricos: false, dieta: false },
};

export function presetParaTipo(tipo: string) {
  return {
    modulosHabilitadosJson: { ...(CLINIC_PRESETS[tipo] ?? ALL_ENABLED) },
    antecedentesSecciones: { ...(ANTECEDENTES_PRESETS[tipo] ?? ALL_ANTECEDENTES_ENABLED) },
  };
}

// Solo los nombres de las plantillas sugeridas por perfil — para el preview en Configuración
// antes de crearlas. El contenido clínico real (esquemaJson) vive únicamente en
// backend/src/common/constants/plantilla-exploracion-presets.constant.ts (fuente única) — ver
// .planning/specs/atomicidad-perfil-clinica-consulta.md. Mantener los nombres en sync.
export const PLANTILLA_EXPLORACION_PRESET_NOMBRES: Record<string, string[]> = {
  dental: [],
  medica_general: [],
  especialidad: [],
  multidisciplinaria: [],
  fisioterapia: ['Evaluación de sesión de fisioterapia'],
  nutricion: ['Consulta nutricional'],
  psicologia: ['Nota de sesión'],
  oftalmologia: ['Examen oftalmológico'],
  dermatologia: ['Evaluación dermatológica'],
};
